import { toISOLanguageCode } from './isoLanguageCode';

/**
 * ChangeLanguageDirectiveController is a class that manages the language change functionality in the application.
 * It handles the selection of system languages, updates the language settings, and reloads the route if necessary.
 */
const SYSTEM_LANGUAGES = [
    { label: "MODULES.CHANGELANGUAGE.LANGUAGES.EN", code: "en" },
    { label: "MODULES.CHANGELANGUAGE.LANGUAGES.FR", code: "fr" },
    { label: "MODULES.CHANGELANGUAGE.LANGUAGES.ES", code: "es" },
];
const SUPPORTED_LANGUAGES = SYSTEM_LANGUAGES.map(l => l.code);
const SESSION_STORAGE_LANGUAGE_KEY = 'systemLanguage';

class ChangeLanguageDirectiveController {
    /**
     * Constructor for ChangeLanguageDirectiveController.
     * @param {Object} $scope - AngularJS scope object. (Still needed for logging/watches if any)
     * @param {Object} $bridges - Service for bridge functionalities.
     * @param {Object} $timeout - AngularJS timeout service.
     * @param {Object} TaskInfoService - Service for task information.
     * @param {Object} $translate - AngularJS translate service.
     * @param {Object} $signals - Service for signal handling.
     * @param {Object} $location - AngularJS location service.
     * @param {Object} $config - Configuration service.
     * @param {Object} $route - AngularJS route service.
     * @param {Object} amMoment - Moment.js adapter for AngularJS.
     */
    constructor($scope, $bridges, $timeout, TaskInfoService, $translate, $signals, $location, $config, $route, amMoment) {
        // Keep services needed by methods
        this.$translate = $translate;
        this.$route = $route;
        this.amMoment = amMoment;
        this.taskInfo = TaskInfoService.getTaskInfo();

        const logger = $bridges.logger();
        logger.logExit($scope, 'initialisation');

        // Initialize controller properties
        this.isCMS = isCMS;
        this.systemLangSelectable = this.taskInfo ? this.taskInfo.isSystemLangSelectable() : true;
        this.language = this._getEffectiveLanguage();
        if (this.taskInfo && this.language !== this.taskInfo.getLanguageCode()) {
            this.taskInfo.setLanguageCode(this.language);
        }
        this.ISOLanguage = this.taskInfo
            ? this.taskInfo.getISOLanguageCode()
            : toISOLanguageCode(this.language);

        this.systemLanguages = SYSTEM_LANGUAGES;

        // Apply persisted language or fall back to default
        if (!angular.isDefined(languages[this.$translate.use()])) {
            this.language = 'en';
            this.onChangeLanguage(true);
        } else if (this.$translate.use() !== this.ISOLanguage) {
            this.onChangeLanguage(true);
        }
    }

    /**
     * Handles the change language event.
     * Updates the language settings and reloads the route if necessary.
     * @param {boolean} forceInit
     */
    onChangeLanguage(forceInit = false) {
        if (!forceInit && !this.systemLangSelectable) return;

        if (this.taskInfo) {
            this.taskInfo.setLanguageCode(this.language);
        }
        if (this.systemLangSelectable) {
            try { sessionStorage.setItem(SESSION_STORAGE_LANGUAGE_KEY, this.language); } catch (e) { /* ignore */ }
        }
        this.amMoment.changeLocale(this.language);

        if (this.taskInfo) {
            this.ISOLanguage = this.taskInfo.getISOLanguageCode();
        } else {
            this.ISOLanguage = toISOLanguageCode(this.language);
        }

        const translateKey = this.isCMS ? 'preview_' + this.ISOLanguage : this.ISOLanguage;

        if (this.$translate.use() !== translateKey) {
            this.$translate.use(translateKey);

            if (!forceInit && this.$route.current && this.$route.current.regexp &&
                !this.$route.current.regexp.test("/invigilatorMenu/:taskUUID/:returnTo") &&
                !this.$route.current.regexp.test("/invigilatorMenu/:taskUUID")) {
                this.$route.reload();
            }
        }
    }

    _getEffectiveLanguage() {
        if (this.taskInfo && !this.systemLangSelectable) {
            return this.taskInfo.getLanguageCode();
        }
        const persisted = this._getPersistedLanguage();
        if (persisted) return persisted;
        if (this.taskInfo) return this.taskInfo.getLanguageCode();
        return 'en';
    }

    _getPersistedLanguage() {
        try {
            const stored = sessionStorage.getItem(SESSION_STORAGE_LANGUAGE_KEY);
            if (stored && SUPPORTED_LANGUAGES.indexOf(stored) !== -1) {
                return stored;
            }
        } catch (e) { /* sessionStorage unavailable */ }
        return null;
    }
}

export default ChangeLanguageDirectiveController;